import camelcaseKeys from 'camelcase-keys'
import { RealtimePostgresChangesPayload } from '@supabase/realtime-js'
import { Database } from '../types/supabase'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { callTool } from '../lib/tools'
import useServices from './useServices'
import { SERVICE_FORBIDDEN, toolRefusalMessage, type RootApproval } from '../utilities/routerWrites'
import { approveRequest } from '../utilities/approveRequest'

export type AccessGrant = {
  id: string
  serviceId: string
  requesterEmail: string
  createdAt: string
  grantedAt: string | null
  // The owner's "no", and it has to outlive the row it was made about: rejection
  // used to be a DELETE, and with the requester now asking on arrival rather than
  // on a click, that let them overturn it by reloading the page.
  rejectedAt: string | null
}

export interface AccessGrantsState {
  grants: AccessGrant[]
  /** The last refused approve, reject or revoke, as one sentence; null after the next success. */
  error: string | null
  /**
   * The request whose approval gives ROOT and waits for the reader's confirmation (app#461), or
   * null. The router asked for it by refusing the first `approve`; the dialog sends the second.
   */
  rootApproval: ({ grant: AccessGrant } & RootApproval) | null
  set: (state: Partial<AccessGrantsState>) => void
  get: (serviceId?: string) => Promise<void>
  add: (grant: AccessGrant) => void
  update: (grant: Partial<AccessGrant>) => void
  remove: (grantId: string) => void
  approve: (grant: AccessGrant, confirmedRoot?: boolean) => Promise<void>
  reject: (grant: AccessGrant) => Promise<void>
  revoke: (serviceId: string, email: string) => Promise<void>
  subscribe: (serviceId?: string) => () => void
  reset: () => void
}

const state = {
  grants: [],
  error: null,
  rootApproval: null,
}

// supabase-js >= 2.110 DEDUPES channels by topic: `channel(name)` hands back an
// already-joined channel instead of a fresh one, and `.on('postgres_changes')`
// on a joined channel THROWS. Two live subscribers is the normal case here —
// useAccessRequestNotifier keeps this store hot on every authenticated page and
// Permissions subscribes on top of it — so the topic has to be unique per
// subscription. Sharing it blanked the whole app: the throw lands in a
// commit-phase effect, and with no ErrorBoundary React unmounts the tree.
let channelSeq = 0

const useAccessGrants = create<AccessGrantsState>()((set, get) => ({
  ...state,
  set,
  get: async serviceId => {
    let query = supabase.from('access_grants').select('*').order('created_at', { ascending: false })
    if (serviceId) query = query.eq('service_id', serviceId)
    const { data, error } = await query
    if (error) {
      console.warn(error)
      return
    }
    const grants = data ? (data.map(g => camelcaseKeys(g)) as AccessGrant[]) : []
    set({ grants })
  },
  add: grant => {
    const grants = [...get().grants]
    if (grants.some(g => g.id === grant.id)) return
    grants.unshift(grant)
    set({ grants })
  },
  update: grant => {
    const grants = get().grants.map(g => (g.id === grant.id ? { ...g, ...grant } : g))
    set({ grants })
  },
  remove: grantId => {
    set({ grants: get().grants.filter(g => g.id !== grantId) })
  },
  // ⚠⚠ APPROVE, REJECT AND REVOKE GO THROUGH THE ROUTER'S `service_share` (app#373,
  // docs/PERMISSIONS.md § 5 Services: "change who may visit — the node's controllers"). They were
  // PostgREST writes that any Member of the org could make, on any service.
  //
  // ⚠ `approve` is ONE router call now: the router adds the requester's address to the list AND
  // stamps the request granted in one transaction (router#1758). The app made the two writes one
  // after the other before, with a 250 ms sleep between them for the services cache, and a failure
  // between the two left a requester on the list with the request still pending. It also clears a
  // rejection, so "Accept after all" is the same call.
  // ⚠ The row shows the answer at once and goes back if the router refuses; realtime then brings
  // the stored row. A refusal is kept in `error` for the page to show, never only in the console.
  //
  // ⚠⚠ ON A ROOT TERMINAL THE FIRST CALL IS REFUSED, AND THAT IS NOT AN ERROR (app#461). Naming a
  // requester who does not control the node gives a root shell (docs/PERMISSIONS.md § 5 Services,
  // D-40), so the router asks for `confirm_root`. The refusal becomes `rootApproval`, the page
  // asks the reader, and the dialog calls again with `confirmedRoot`. Before this the refusal was
  // shown as an error and Accept could never succeed.
  // ⚠ The sequence is `approveRequest` (`utilities/approveRequest.ts`), which has the rules and a
  // test for each (app#467). The store gives it the row, the router call and the page's state.
  approve: async (grant, confirmedRoot = false) =>
    approveRequest(grant, confirmedRoot, useServices.getState().services.find(s => s.id === grant.serviceId), {
      live: id => get().grants.find(g => g.id === id),
      update: answer => get().update(answer),
      call: args => callTool('service_share', args),
      openRootId: () => get().rootApproval?.grant.id ?? null,
      ask: root => set({ rootApproval: { grant, ...root } }),
      say: (message, cause) => {
        if (cause) console.warn('Approve error:', cause)
        set({ error: message })
      },
      refetch: () => void get().get(),
      now: () => new Date().toISOString(),
    }),
  // An UPDATE on the router's side, not a DELETE: a rejection has to outlive the request, or the
  // requester asks again by reloading the access page.
  reject: async grant => {
    get().update({ id: grant.id, rejectedAt: new Date().toISOString() })
    set({ error: null })
    try {
      await callTool('service_share', { service_id: grant.serviceId, action: 'reject', grant_id: grant.id })
    } catch (e) {
      console.warn('Reject error:', e)
      // ⚠ Roll back THIS ROW only, never a snapshot of the array: realtime may have delivered
      // another requester's row during the call.
      get().update({ id: grant.id, rejectedAt: grant.rejectedAt })
      set({ error: toolRefusalMessage(e, SERVICE_FORBIDDEN, 'Could not reject the request.') })
    }
  },
  // Takes one address off the list (`remove`), spelled as the list stores it.
  revoke: async (serviceId, email) => {
    set({ error: null })
    try {
      await callTool('service_share', { service_id: serviceId, action: 'remove', emails: [email] })
    } catch (e) {
      console.warn('Revoke error:', e)
      set({ error: toolRefusalMessage(e, SERVICE_FORBIDDEN, 'Could not remove the address.') })
    }
  },
  subscribe: serviceId => {
    const { add, update, remove } = get()
    const channelName = `access_grants-${serviceId ?? 'all'}-${++channelSeq}`
    const filter = serviceId ? `service_id=eq.${serviceId}` : undefined

    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'access_grants', filter },
        (payload: RealtimePostgresChangesPayload<Database['public']['Tables']['access_grants']['Row']>) => {
          switch (payload.eventType) {
            case 'INSERT': {
              const grant = camelcaseKeys(payload.new) as AccessGrant
              add(grant)
              break
            }
            case 'UPDATE': {
              const grant = camelcaseKeys(payload.new) as AccessGrant
              update(grant)
              break
            }
            case 'DELETE':
              if (payload.old.id) remove(payload.old.id)
              break
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(subscription)
    }
  },
  reset: () => set({ ...state }),
}))

export default useAccessGrants
